import { useEffect, useState } from "react";
import { http, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { UserCheck } from "lucide-react";

export default function ResponsibleManagerCard({ kind, targetId, value, activeStay = false, canEdit = false, onSaved }) {
  const [managers, setManagers] = useState([]);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState("");
  const [reassign, setReassign] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get("/admin/staff/managers").then((r) => setManagers(r.data.managers || [])).catch(() => {});
  }, []);

  const openDialog = () => { setSel(value?.id || ""); setReassign(true); setOpen(true); };

  const save = async () => {
    setBusy(true);
    try {
      const r = await http.put(`/admin/${kind}/${targetId}/responsible-manager`, { manager_id: sel || null, reassign_open: activeStay && reassign });
      const extra = r.data.reassigned_open_items ? ` · ${r.data.reassigned_open_items} open item(s) reassigned` : "";
      toast.success(`Responsible Manager: ${r.data.responsible_manager?.name || "UNASSIGNED"}${extra}`);
      setOpen(false);
      onSaved && onSaved();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card" data-testid="rm-card">
      <h3 className="font-serif text-2xl mb-1 flex items-center gap-2"><UserCheck size={20} style={{ color: "var(--pine)" }} /> Responsible Manager</h3>
      <p className="text-xs mb-3" style={{ color: "var(--muted)" }}>New food orders &amp; service requests for this stay auto-assign to this manager.</p>
      <div className="flex items-center justify-between gap-3">
        {value ? (
          <span className="font-medium" data-testid="rm-current">{value.name}</span>
        ) : (
          <span className="badge-terracotta" data-testid="rm-unassigned">UNASSIGNED</span>
        )}
        {canEdit && <button data-testid="rm-change" onClick={openDialog} className="btn-outline" style={{ padding: "6px 14px", fontSize: 13 }}>{value ? "Change" : "Assign"}</button>}
      </div>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.45)" }} data-testid="rm-dialog"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div className="card max-w-md w-full">
            <h3 className="font-serif text-2xl mb-4">Responsible Manager</h3>
            <select data-testid="rm-select" value={sel} onChange={(e) => setSel(e.target.value)} className="mb-3">
              <option value="">Unassigned</option>
              {managers.map((m) => <option key={m.id} value={m.id}>{m.name} · {m.email}</option>)}
            </select>
            {activeStay && (
              <label className="flex items-start gap-2 text-sm mb-4 cursor-pointer">
                <input type="checkbox" style={{ width: "auto" }} data-testid="rm-reassign" checked={reassign} onChange={(e) => setReassign(e.target.checked)} />
                Also reassign open/pending items to the new Responsible Manager (completed history stays untouched)
              </label>
            )}
            <div className="flex gap-3">
              <button data-testid="rm-save" onClick={save} disabled={busy} className="btn-primary flex-1">{busy ? "Saving…" : "Save"}</button>
              <button data-testid="rm-cancel" onClick={() => setOpen(false)} className="btn-outline flex-1">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
